import React, { useCallback, useEffect, useState } from 'react';
import {
  Server, Cpu, HardDrive, MemoryStick, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, ChevronLeft, ChevronRight,
  Activity, Clock,
} from 'lucide-react';
import { useApi } from '../../lib/api';

const LOG_PAGE_SIZE = 25;

export default function ServerHealth() {
  const api = useApi();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logSearch, setLogSearch] = useState('');
  const [todayOnly, setTodayOnly] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminServerHealth(todayOnly ? { since: 'today' } : undefined);
      setData(res);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, todayOnly]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10s
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Refresh immediately when tab becomes visible
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const filteredLogs = (data?.error_lines || []).filter(l =>
    !logSearch || l.toLowerCase().includes(logSearch.toLowerCase())
  );
  const logTotalPages = Math.max(1, Math.ceil(filteredLogs.length / LOG_PAGE_SIZE));
  const pagedLogs = filteredLogs.slice((logPage - 1) * LOG_PAGE_SIZE, logPage * LOG_PAGE_SIZE);
  const logPageNums = buildPageNums(logPage, logTotalPages);

  const sys = data?.system;
  const processes = data?.processes || [];

  const fmtUptime = (s) => {
    if (!s) return '—';
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const fmtProcUptime = (ms) => {
    if (!ms) return '—';
    return fmtUptime(Math.floor(ms / 1000));
  };

  const statusIcon = (status) => {
    if (status === 'online')   return <CheckCircle2 size={14} style={{ color: '#16a34a' }} />;
    if (status === 'stopped')  return <XCircle size={14} style={{ color: '#6b7280' }} />;
    return <AlertTriangle size={14} style={{ color: '#dc2626' }} />;
  };

  const statusPill = (status) => {
    if (status === 'online')  return <span className="admin-status-pill ok">online</span>;
    if (status === 'stopped') return <span className="admin-status-pill skipped">stopped</span>;
    return <span className="admin-status-pill failed">{status}</span>;
  };

  const gauge = (pct, warn = 70, danger = 90) => {
    const color = pct >= danger ? '#ef4444' : pct >= warn ? '#f97316' : '#3b82f6';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
        </div>
        <span style={{ fontSize: 12, color, fontWeight: 700, minWidth: 36, textAlign: 'right' }}>{pct}%</span>
      </div>
    );
  };

  if (error) return <div className="admin-banner error">{error}</div>;
  if (!data)  return <div style={{ color: '#9ca3af', padding: 32 }}>Loading…</div>;

  const unhealthy = processes.filter(p => p.status !== 'online').length;

  return (
    <>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Server Health</h1>
          <p className="admin-page-subtitle">
            Live system metrics, PM2 process status, and recent error logs.
            {lastRefresh && (
              <span style={{ marginLeft: 10, color: '#9ca3af', fontSize: 12 }}>
                Last refreshed {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="admin-header-actions">
          <button className="btn-secondary" onClick={load} disabled={loading} style={{ gap: 6 }}>
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="admin-kpi-row">
        <div className="admin-kpi">
          <div className="admin-kpi-icon blue"><Cpu size={18} /></div>
          <p className="admin-kpi-label">CPU usage</p>
          <p className="admin-kpi-value">{sys.cpu_pct}%</p>
          <p className="admin-kpi-sub">Load avg {sys.load_avg?.[0]} · {sys.load_avg?.[1]} · {sys.load_avg?.[2]}</p>
        </div>
        <div className={`admin-kpi ${sys.mem.pct >= 90 ? 'danger' : sys.mem.pct >= 70 ? 'warn' : ''}`}>
          <div className={`admin-kpi-icon ${sys.mem.pct >= 90 ? 'red' : sys.mem.pct >= 70 ? 'orange' : 'green'}`}>
            <MemoryStick size={18} />
          </div>
          <p className="admin-kpi-label">Memory</p>
          <p className="admin-kpi-value">{sys.mem.used_mb} MB</p>
          <p className="admin-kpi-sub">of {sys.mem.total_mb} MB · {sys.mem.pct}% used</p>
        </div>
        {sys.disk ? (
          <div className={`admin-kpi ${sys.disk.pct >= 90 ? 'danger' : sys.disk.pct >= 70 ? 'warn' : ''}`}>
            <div className={`admin-kpi-icon ${sys.disk.pct >= 90 ? 'red' : sys.disk.pct >= 70 ? 'orange' : 'teal'}`}>
              <HardDrive size={18} />
            </div>
            <p className="admin-kpi-label">Disk</p>
            <p className="admin-kpi-value">{sys.disk.used_mb} MB</p>
            <p className="admin-kpi-sub">of {sys.disk.total_mb} MB · {sys.disk.pct}% used</p>
          </div>
        ) : (
          <div className="admin-kpi">
            <div className="admin-kpi-icon teal"><HardDrive size={18} /></div>
            <p className="admin-kpi-label">Disk</p>
            <p className="admin-kpi-value">—</p>
            <p className="admin-kpi-sub">Not available</p>
          </div>
        )}
        <div className={`admin-kpi ${unhealthy > 0 ? 'danger' : 'ok'}`}>
          <div className={`admin-kpi-icon ${unhealthy > 0 ? 'red' : 'green'}`}>
            <Activity size={18} />
          </div>
          <p className="admin-kpi-label">Processes</p>
          <p className="admin-kpi-value">{processes.length}</p>
          <p className="admin-kpi-sub">{unhealthy > 0 ? `${unhealthy} unhealthy` : 'All online'}</p>
        </div>
      </div>

      {/* System info + gauges */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="admin-card" style={{ margin: 0 }}>
          <div className="admin-card-header"><h2>System info</h2></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
            {[
              ['Hostname', sys.hostname],
              ['Platform', sys.platform],
              ['Architecture', sys.arch],
              ['Server uptime', fmtUptime(sys.uptime_s)],
            ].map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, color: '#0f1c2e', fontWeight: 600 }}>{val}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card" style={{ margin: 0 }}>
          <div className="admin-card-header"><h2>Resource usage</h2></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151', marginBottom: 2 }}>
                <span>CPU</span><span style={{ color: '#6b7280' }}>{sys.cpu_pct}%</span>
              </div>
              {gauge(sys.cpu_pct)}
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151', marginBottom: 2 }}>
                <span>Memory</span><span style={{ color: '#6b7280' }}>{sys.mem.used_mb} / {sys.mem.total_mb} MB</span>
              </div>
              {gauge(sys.mem.pct)}
            </div>
            {sys.disk && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151', marginBottom: 2 }}>
                  <span>Disk</span><span style={{ color: '#6b7280' }}>{sys.disk.used_mb} / {sys.disk.total_mb} MB</span>
                </div>
                {gauge(sys.disk.pct, 75, 90)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PM2 processes table */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>PM2 processes ({processes.length})</h2>
          {unhealthy > 0 && (
            <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
              {unhealthy} process{unhealthy > 1 ? 'es' : ''} not online
            </span>
          )}
        </div>
        {processes.length === 0 ? (
          <div className="admin-empty">No PM2 processes found.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>NAME</th>
                <th>STATUS</th>
                <th>PID</th>
                <th style={{ textAlign: 'right' }}>CPU</th>
                <th style={{ textAlign: 'right' }}>MEMORY</th>
                <th style={{ textAlign: 'right' }}>RESTARTS</th>
                <th style={{ textAlign: 'right' }}>UPTIME</th>
              </tr>
            </thead>
            <tbody>
              {processes.map(p => (
                <tr key={p.id}>
                  <td style={{ color: '#9ca3af', fontSize: 12 }}>{p.id}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {statusIcon(p.status)}
                      <span style={{ fontWeight: 600, color: '#0f1c2e', fontSize: 13 }}>{p.name}</span>
                    </div>
                  </td>
                  <td>{statusPill(p.status)}</td>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>{p.pid || '—'}</td>
                  <td style={{ textAlign: 'right', fontSize: 13, color: p.cpu > 50 ? '#dc2626' : '#374151', fontWeight: p.cpu > 50 ? 700 : 400 }}>
                    {p.cpu != null ? `${p.cpu}%` : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 13, color: '#374151' }}>
                    {p.memory_mb != null ? `${p.memory_mb} MB` : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      color: p.restarts > 5 ? '#dc2626' : p.restarts > 0 ? '#f97316' : '#16a34a',
                    }}>
                      {p.restarts}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 12, color: '#6b7280' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      <Clock size={11} />
                      {fmtProcUptime(p.uptime_ms)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Traffic panel */}
      {data.traffic && (() => {
        const tr = data.traffic;
        const maxCount = Math.max(1, ...tr.history.map(h => h.count));
        return (
          <div className="admin-card">
            <div className="admin-card-header" style={{ marginBottom: 16 }}>
              <h2>Request traffic</h2>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>Live — updates every 10s</span>
            </div>

            {/* Traffic KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Active requests', value: tr.active_requests, color: tr.active_requests > 20 ? '#dc2626' : tr.active_requests > 5 ? '#f97316' : '#16a34a', suffix: '' },
                { label: 'Requests / min (now)', value: tr.req_this_min, color: '#3b82f6', suffix: '' },
                { label: 'Requests / min (prev)', value: tr.req_last_min, color: '#6b7280', suffix: '' },
                { label: 'Avg response time', value: tr.avg_response_ms, color: tr.avg_response_ms > 1000 ? '#dc2626' : tr.avg_response_ms > 500 ? '#f97316' : '#16a34a', suffix: ' ms' },
              ].map(({ label, value, color, suffix }) => (
                <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 16px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}{suffix}</div>
                </div>
              ))}
            </div>

            {/* Sparkline */}
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Requests per minute — last 10 minutes</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 60 }}>
                {tr.history.map((h, i) => {
                  const barH = maxCount ? Math.max(4, Math.round((h.count / maxCount) * 56)) : 4;
                  const isNow = i === tr.history.length - 1;
                  const hasErr = h.errors > 0;
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }} title={`${h.count} req${h.errors ? `, ${h.errors} errors` : ''}`}>
                      <div style={{ width: '100%', height: barH, background: hasErr ? '#fca5a5' : isNow ? '#3b82f6' : '#bfdbfe', borderRadius: '3px 3px 0 0', transition: 'height 0.3s' }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#d1d5db', marginTop: 4 }}>
                <span>10 min ago</span><span>now</span>
              </div>
              {tr.total_errors_10min > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 12px' }}>
                  ⚠ {tr.total_errors_10min} server error{tr.total_errors_10min > 1 ? 's' : ''} (5xx) in the last 10 minutes
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Job queues */}
      <div className="admin-card">
        <div className="admin-card-header" style={{ marginBottom: 16 }}>
          <h2>Job queues</h2>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>BullMQ — Redis-backed async workers</span>
        </div>
        {data?.queues == null ? (
          <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0' }}>Unavailable — Redis not connected</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['Queue', 'Waiting', 'Active', 'Completed', 'Failed', 'Delayed'].map(h => (
                    <th key={h} style={{ textAlign: h === 'Queue' ? 'left' : 'right', padding: '6px 12px', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.queues).map(([name, s]) => (
                  <tr key={name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600, textTransform: 'capitalize' }}>{name}</td>
                    {s == null ? (
                      <td colSpan={5} style={{ padding: '8px 12px', color: '#9ca3af', textAlign: 'center' }}>unavailable</td>
                    ) : (
                      <>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: s.waiting > 0 ? '#f97316' : '#6b7280', fontWeight: s.waiting > 0 ? 700 : 400 }}>{s.waiting}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: s.active > 0 ? '#3b82f6' : '#6b7280', fontWeight: s.active > 0 ? 700 : 400 }}>{s.active}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: s.completed > 0 ? '#16a34a' : '#6b7280' }}>{s.completed}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: s.failed > 0 ? '#dc2626' : '#6b7280', fontWeight: s.failed > 0 ? 700 : 400 }}>{s.failed}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: s.delayed > 0 ? '#7c3aed' : '#6b7280' }}>{s.delayed}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Error log */}
      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            Error log
            <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: '#9ca3af' }}>
              ({filteredLogs.length} entries)
            </span>
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => { setTodayOnly(v => !v); setLogPage(1); }}
              style={{
                font: 'inherit', fontSize: 12, fontWeight: 600, padding: '6px 12px',
                border: `1px solid ${todayOnly ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                background: todayOnly ? '#eff6ff' : 'white',
                color: todayOnly ? '#2563eb' : '#6b7280',
              }}
            >
              {todayOnly ? '📅 Today only' : 'All days'}
            </button>
            <input
              placeholder="Search errors..."
              value={logSearch}
              onChange={e => { setLogSearch(e.target.value); setLogPage(1); }}
              autoComplete="off"
              style={{ font: 'inherit', fontSize: 13, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, width: 220, outline: 'none', color: '#374151' }}
            />
          </div>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="admin-empty" style={{ color: '#16a34a' }}>
            {data.error_lines.length === 0 ? '✓ No errors found in log.' : 'No lines match your search.'}
          </div>
        ) : (
          <div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>TIME</th>
                  <th style={{ width: 80 }}>LEVEL</th>
                  <th>MESSAGE</th>
                  <th style={{ width: 200 }}>SOURCE</th>
                </tr>
              </thead>
              <tbody>
                {pagedLogs.map((line, i) => {
                  const parsed = parseLogLine(line);
                  return (
                    <tr key={i}>
                      <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{parsed.time}</td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          fontSize: 11, fontWeight: 700,
                          padding: '2px 8px', borderRadius: 4,
                          background: parsed.level === 'ERROR' ? '#fef2f2' : parsed.level === 'WARN' ? '#fffbeb' : '#f0f9ff',
                          color: parsed.level === 'ERROR' ? '#dc2626' : parsed.level === 'WARN' ? '#d97706' : '#0369a1',
                        }}>
                          {parsed.level}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#111827', maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span title={parsed.message}>{parsed.message}</span>
                      </td>
                      <td style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                        <span title={parsed.source}>{parsed.source}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Showing {Math.min((logPage - 1) * LOG_PAGE_SIZE + 1, filteredLogs.length)}–{Math.min(logPage * LOG_PAGE_SIZE, filteredLogs.length)} of {filteredLogs.length}
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button onClick={() => setLogPage(p => Math.max(1, p - 1))} disabled={logPage === 1} style={pageBtn(logPage === 1)}>
                  <ChevronLeft size={14} />
                </button>
                {logPageNums.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                    : <button key={p} onClick={() => setLogPage(p)} style={pageBtn(false, logPage === p)}>{p}</button>
                )}
                <button onClick={() => setLogPage(p => Math.min(logTotalPages, p + 1))} disabled={logPage === logTotalPages} style={pageBtn(logPage === logTotalPages)}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </>
  );
}

function parseLogLine(line) {
  // Extract timestamp — ISO format (PM2/app logs, or journalctl with -o short-iso)
  // falling back to classic syslog "Mon DD HH:MM:SS" (no year) for any log
  // source still emitting that format, so TIME doesn't silently show "—".
  let time = '—';
  const isoMatch = line.match(/(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})/);
  const syslogMatch = !isoMatch && line.match(/^([A-Z][a-z]{2}\s+\d{1,2}\s+\d{2}:\d{2}:\d{2})/);
  if (isoMatch) {
    try {
      const d = new Date(isoMatch[1]);
      time = d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    } catch (_) { time = isoMatch[1]; }
  } else if (syslogMatch) {
    try {
      const d = new Date(`${syslogMatch[1]} ${new Date().getFullYear()}`);
      time = isNaN(d.getTime()) ? syslogMatch[1] : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    } catch (_) { time = syslogMatch[1]; }
  }

  // Determine level
  let level = 'INFO';
  if (/error|exception|fatal|uncaught|unhandled/i.test(line)) level = 'ERROR';
  else if (/warn|warning|deprecated/i.test(line)) level = 'WARN';

  // Extract source file (at path/file.js:line)
  let source = '';
  const srcMatch = line.match(/at\s+[\w.<>]+\s+\(([^)]+)\)/) || line.match(/at\s+(\/[^\s:]+:\d+)/);
  if (srcMatch) {
    // shorten: remove full path prefix, keep last 2 segments + line
    const full = srcMatch[1];
    const parts = full.split('/');
    source = parts.slice(-2).join('/');
  }

  // Clean message: strip timestamp, stack trace lines (starting with "at "), brackets
  let message = line
    .replace(/\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?/g, '')
    .replace(/^\s*\d+\s*\|?\s*/, '')  // PM2 prefix like "0|backend |"
    .replace(/^[^|]+\|\s*/, '')
    .replace(/\bat\s+[\w.<> /\\]+:\d+:\d+\b/g, '')
    .replace(/\(internal\/[^)]+\)/g, '')
    .trim();

  if (!message) message = line.trim();

  return { time, level, message, source };
}

function buildPageNums(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1, 2, 3];
  if (cur > 3 && cur < total - 1) { pages.push('…', cur); }
  pages.push('…', total);
  return [...new Set(pages)];
}

function pageBtn(disabled, active) {
  return {
    width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6,
    background: active ? '#0f1c2e' : 'white',
    color: disabled ? '#d1d5db' : active ? 'white' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
